import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { DSSetup, PopupListItem, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { copyObject, isAlive } from 'webapp/utils/common';
import { scaleValToAutofitJQ } from 'webapp/utils/html';

import joinClasses from 'webapp/helpers/join-classes';
import didCacheUpdate from 'webapp/modifiers/did-cache-update';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import CommonGenieButtonComponent from 'webapp/components/common/genie/button';
import CommonSkeletonBarLoaderComponent from 'webapp/components/common/skeleton-bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataStatusIconComponent from 'webapp/components/data-status-icon';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import FormRangeInlineComponent from 'webapp/components/form/range/inline';
import MetricsChartComponent from 'webapp/components/metrics/chart';
import SubmitButtonComponent from 'webapp/components/submit-button';
import TeamsViewSectionRestrictedIconComponent from 'webapp/components/teams/view/section/restricted-icon';
import ToolTipComponent from 'webapp/components/tool-tip';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

import type { DateRange, TeamSectionItem } from 'webapp/models-custom';

import type { Action as TeamAction } from 'webapp/services/team';

type Action = 'changeMetric' | TeamAction;

type Option = PopupListItem<Action>;

interface Args {
	model: TeamSectionItem; // only metric type
	hideSort?: boolean;
	removeFromSection?: (item: TeamSectionItem) => void;
	isRestricted?: boolean;
}

export default class TeamViewSectionMetricsGridItemComponent extends Component<Args> {
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('popup') declare popupService: Service.Popup;
	@service('account') declare accountService: Service.Account;
	@service('timer') declare timerService: Service.Timer;
	@service('team') declare teamService: Service.Team;
	@service('model-manager/team-sections')
	declare mmTeamSectionService: Service.ModelManager.TeamSections;
	@service('metric-details')
	declare metricDetailsService: Service.MetricDetails;
	@service('navigation') declare navigationService: Service.Navigation;

	@tracked isContextOpen = false;
	@tracked isDateRangePopupOpen = false;
	@tracked isDataPerformanceSummaryPopupOpen = false;
	@tracked isVisibleOnScreen = false;

	node: HTMLElement | null = null;

	get model() {
		return this.args.model;
	}

	get isDemoAccount() {
		return this.accountService.isDemoAccount;
	}

	get showNumberLoader() {
		return this.model.isLoadingData || this.model.isNumberSyncing;
	}

	get isSomethingOpen() {
		return this.isContextOpen || this.isDataPerformanceSummaryPopupOpen;
	}

	get options(): Option[] {
		const options: Option[] = [];

		options.push({
			icon: 'Refresh',
			text: 'Change metric',
			action: 'changeMetric',
		});

		options.push(...this.teamService.getTeamSectionItemOptions(true));

		return options;
	}

	get canEdit() {
		return !this.isDemoAccount && this.model.isCurrentUserMember;
	}

	get isDateRangeSelectDisabled() {
		return !this.canEdit;
	}

	get showActions() {
		return this.model.isCurrentUserMember;
	}

	get sourceObj() {
		return this.model.source;
	}

	get chartHeight(): number {
		return this.navigationService.isMobileApp ? 30 : 37;
	}

	get showAiChatButton(): boolean {
		return this.aiChatService.isEligibleForAiChat;
	}

	@action
	updateDataCache() {
		this.args.model.visualization?.checkToUpdateDataCache();
	}

	@action
	changeMetric() {
		if (this.accountService.handleDemo()) {
			return;
		}

		const model = this.model,
			section = model.section,
			updateMetric = (metricSetupData: DSSetup.MetricSetupData) => {
				model.metricSetup.setFromMetricSetupData(metricSetupData);
				this.model.updateObjectDataAndSave();
			};

		if (section) {
			this.mmTeamSectionService.openChangeMetricPopup(
				section,
				updateMetric,
				model.metricSetup.metricSetupData,
				'Change',
			);
		}
	}

	@action
	removeFromSection() {
		if (this.accountService.handleDemo()) {
			return;
		}

		const teamSectionItem = this.model;

		if (teamSectionItem) {
			this.args.removeFromSection?.(teamSectionItem);
		}
	}

	@action
	viewInsights() {
		const model = this.model;
		this.metricDetailsService.openMetricDetailsDrawer(
			{
				metricSetup: model.metricSetup,
				dateRange: model.dateRange,
			},
			'teams',
		);
	}

	@action
	changeDateRangePopupOpenState(isOpen: boolean) {
		this.isDateRangePopupOpen = isOpen;
	}

	@action
	changeDataPerformanceSummaryPopupOpenState(isOpen: boolean) {
		this.isDataPerformanceSummaryPopupOpen = isOpen;
	}

	@action
	dateRangeChanged(selectedRange: DateRange) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const model = this.model;
		const limitations = model.limitations;

		limitations.updateDateRangeFromFormula(selectedRange.formula);
		limitations.selectOptimalValidGranulation();

		if (limitations.dateRange?.formula) {
			model.directPropUpdate({
				dateRange: copyObject(limitations.dateRange.getFormula()),
			});
		}

		model.updateObjectDataAndSave();
	}

	@action
	openContext(e: PointerEvent) {
		if (this.isContextOpen) {
			return;
		}

		const attachTo = e.target as HTMLElement;

		if (!attachTo) {
			return;
		}

		const popup = this.popupService.createListPopup(attachTo);

		if (popup) {
			popup.open({
				itemsList: this.options,
				doAction: this.doOptionsAction,
			});

			this.isContextOpen = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.isContextOpen = false;
				}
			});
		}
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node;
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	@action
	scaleValToAutofit() {
		if (!this.isVisibleOnScreen || !this.node) {
			return;
		}

		const ct = $(this.node),
			el = ct.find('.number .wrap'),
			minSizeEm = 0.5,
			maxSizeEm = 1;

		if (el.length) {
			scaleValToAutofitJQ(el, minSizeEm, maxSizeEm, ct.find('.number'));
		}
	}

	@action
	openGenie() {
		this.aiChatService.openGenieForMetricBlock(this.model);
	}
	<template>
		<li
			data-id={{@model.id}}
			class={{joinClasses
				(array
					"card"
					"metric-item"
					"card--visualization"
					"card--my-metric"
					"flex-item"
					"filter-sort-item"
					(if this.isSomethingOpen "contextOpened")
					(if this.isDateRangePopupOpen "dateRangeOpened")
					(if @hideSort "hideSort")
				)
			}}
			{{didInsert this.setupView}}
			{{didUpdate this.scaleValToAutofit @model.numberPoint}}
			{{didCacheUpdate this.updateDataCache @model.block.data}}
		>

			{{#if this.isVisibleOnScreen}}

				{{#if this.canEdit}}
					<div class="drag" data-goal-id={{@model.id}}>
						<CommonUiIconComponent @name="ArrowsMove" @size="md" />
					</div>
				{{/if}}

				<div class="top">
					<div class="top__left">
						<FormRangeInlineComponent
							@metricLimitations={{@model.limitations}}
							@onChange={{this.dateRangeChanged}}
							@labelText=""
							@isDisabled={{this.isDateRangeSelectDisabled}}
							@onPopupOpenStateChange={{this.changeDateRangePopupOpenState}}
							@showScrollableLabel={{true}}
						/>
					</div>

					{{#if this.showActions}}
						<div class="top__right">
							<VerificationBadgeComponent
								@verification={{@model.metricSetup.metric.verificationInfo}}
								@entityLabel="metric"
								@size="sm"
							/>
							{{#if @isRestricted}}
								<TeamsViewSectionRestrictedIconComponent />
							{{/if}}

							<div role="button" class="context" {{on "click" this.openContext}}>
								<CommonUiIconComponent @name="Ellipsis" @size="lg" />
							</div>
						</div>
					{{/if}}
				</div>

				<div class="info">
					<div class="anti-stretch">
						<EditableModelPropertyComponent
							@model={{@model}}
							@canEdit={{false}}
							@editableProperty="shownTitle"
						/>
					</div>
				</div>

				<div class="visualization">

					<div class="number">
						<div class="wrap">
							{{#if this.showNumberLoader}}
								<CommonSkeletonBarLoaderComponent
									@type="number"
									@position="center"
								/>
							{{else}}
								<span class="val">
									{{#if @model.hasNumberValue}}
										{{#unless @model.hideNumberValue}}
											{{@model.numberValueStr}}
										{{/unless}}
									{{else}}
										-
									{{/if}}
								</span>
							{{/if}}
						</div>
					</div>

					<div class="preview preview-chart colorTheme-6">
						{{#if @model.isLoadingData}}
							<CommonSkeletonBarLoaderComponent @type="chart" @position="center" />
						{{else}}
							{{#if @model.visualization.series}}
								<MetricsChartComponent
									@visualization={{@model.visualization}}
									@indicatorClass={{@model.indicatorClass}}
									@height={{this.chartHeight}}
									@width="100%"
									@annotationContext={{@model.context}}
									@canAnnotate={{false}}
								/>
							{{/if}}
						{{/if}}
					</div>

					<div class="compare-action-area">
						{{#if @model.changeString}}
							<span class="change">
								<span class="indicator {{@model.indicatorClass}}">
									{{@model.changeString}}
								</span>
								{{#if @model.previousValueString}}
									({{@model.previousValueString}})
								{{/if}}
							</span>
						{{/if}}

						{{#if this.showActions}}
							<div class="compare-action-area__actions">
								<SubmitButtonComponent
									@text="View"
									@onTrigger={{this.viewInsights}}
									@noDelay={{true}}
								/>
								{{#if this.showAiChatButton}}
									<CommonGenieButtonComponent @openChat={{this.openGenie}} />
								{{/if}}
							</div>
						{{/if}}
					</div>

				</div>

				<div class="bottom">

					<div class="source">
						<img
							src={{this.sourceObj.realAvatar}}
							width="16"
							height="16"
							alt={{this.sourceObj.sourceType.name}}
						/>
						<span>
							{{this.sourceObj.title}}
							<ToolTipComponent @text={{this.sourceObj.title}} />
						</span>
						{{#if @model.showDataStatusIcon}}
							<DataStatusIconComponent
								@context="metric"
								@lastSync={{@model.lastSyncFromData}}
								@statusObjects={{@model.dataErrorStatusObjects}}
								@theme="light"
							/>
						{{/if}}
					</div>

				</div>

			{{else}}
				<div class="info" {{visibleCheckItem this.visibleChanged}}>
					<div class="anti-stretch">&nbsp;</div>
				</div>
			{{/if}}

		</li>
	</template>
}
